import type { Control, FieldValues, Path } from 'react-hook-form';
import { SelectField } from '@/components/ui';
import { SELECT_OPTIONS } from '@/constants';
import { formatIDR } from '@/utils';
import type { GiftCardApplication } from '@/hooks';

// Vouchers are no longer a payment method you pick — they're a code you scan.
// Filter `voucher` out of new-membership dropdowns. Legacy rows still render
// fine because the underlying SELECT_OPTIONS constant is untouched.
const PAYMENT_METHODS_FOR_NEW_MEMBERSHIP = SELECT_OPTIONS.paymentMethod.filter(
  (m) => m.value !== 'voucher',
);

interface GiftCardPaymentMethodFieldProps<TFormData extends FieldValues> {
  /** State from useGiftCardApplication — decides coverage and the label. */
  application: GiftCardApplication;
  control: Control<TFormData>;
  name: Path<TFormData>;
  error?: string;
}

/**
 * Payment-method select for the new-membership forms, aware of gift-card
 * coverage. Used by both /memberships/new and the in-modal
 * CreateMembershipModal so the two can't drift.
 *
 * When the card covers the full price the field is disabled and NOT required —
 * `payment_method` is intentionally NULL in that case (the DB column is
 * nullable for exactly this). **The exemption is carried by `disabled`, never
 * by swapping `rules`**: RHF's useController captures rules in a useRef on the
 * first render and never re-reads them, so a `rules` prop toggled after mount
 * is silently ignored and the original `required` keeps firing. That shipped
 * once already — it made a fully-gift-card-paid membership impossible to
 * create, with "Payment method is required" showing under a field the UI had
 * disabled and labelled "Not required".
 */
export default function GiftCardPaymentMethodField<TFormData extends FieldValues>({
  application,
  control,
  name,
  error,
}: GiftCardPaymentMethodFieldProps<TFormData>) {
  const { fullCoverage, appliedCard, remainder } = application;

  return (
    <SelectField<TFormData>
      label={
        fullCoverage
          ? 'Payment Method (fully paid by gift card)'
          : appliedCard
            ? `Remainder (${formatIDR(remainder)}) paid via`
            : 'Payment Method'
      }
      required={!fullCoverage}
      placeholder={
        fullCoverage ? 'Not required — fully paid by gift card' : 'Select payment method'
      }
      name={name}
      control={control}
      options={[...PAYMENT_METHODS_FOR_NEW_MEMBERSHIP]}
      rules={{ required: 'Payment method is required' }}
      error={error}
      disabled={fullCoverage}
    />
  );
}
